// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardDesignerToolbar.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import classnames from 'classnames';
import { cloneDeep, get, getOr, invokeArgs, isEmpty, isNil, omit } from 'lodash/fp';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';
import DashboardConstants from 'logic/data/Izenda.DashboardConstants';
import { EMAIL_TYPE } from 'logic/data/Izenda.EmailConstants';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import uiState from 'logic/Izenda.UiState';
import { action } from 'mobx';
import {
  BackgroundImageContent,
  constants as backgroundImageConstants,
  propTypes as backgroundImagePropTypes
} from 'modules/Dashboard/DashboardDesigner/BackgroundImage/Izenda.BackgroundImageContent';
import {
  constants as dashboardDescriptionConstants,
  DashboardDescriptionContent,
  propTypes as dashboardDescriptionPropTypes
} from 'modules/Dashboard/DashboardDesigner/DashboardDescription/Izenda.DashboardDescriptionContent';
import commonPopup from 'modules/Dashboard/DashboardDesigner/Izenda.CommonDashboardPopup';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Link } from 'react-router';
import EmailPopup from 'share/components/controls/CommonPopup/Izenda.EmailPopup';
import ActionButton from 'share/components/controls/Izenda.ActionButton';
import Button from 'share/components/controls/Izenda.Button';
import ColorPickerPopup from 'share/components/controls/Izenda.ColorPickerPopup';
import ExportButton from 'share/components/controls/Izenda.ExportButton';
import ActionPopup from 'share/components/controls/SavePopup/Izenda.ActionPopup';
import SavePopup from 'share/components/controls/SavePopup/Izenda.SavePopup';
import connect from 'share/enhancers/Izenda.Connect';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import PrintDashboardButton from '../Izenda.PrintDashboardButton';
import actions from './Izenda.DashboardDesignerToolbarAction';
import SubscriptionDetails from 'logic/models/Izenda.SubscriptionDetails';
import RefreshDataButton from '../../Reports/Buttons/Izenda.RefreshDataButton';

const ToggleButton = connect()(function(props: any) {
  const {
    store: { dashboardDesignerMode: buttonStatus },
    label
  } = props;
  const isActive = props.checkActive(buttonStatus);
  const isDisabled = props.checkDisable(buttonStatus);

  return (
    <Button
      className={classnames('btn-report-default', { 'izenda-Button_active': isActive })}
      disabled={isDisabled}
      label={label}
      onClick={props.toggleAction}
    />
  );
});

const CancelButton = connect()(function(props: any) {
  const { store, localization } = props;

  const cancelDashboard = function() {
    const { loader } = props;

    loader.startLoading();

    return props
      .confirmDirtyForm(store.isDirty)
      .then(store.cancelDashboard.bind(store))
      .finally(loader.endLoading.bind(loader));
  };

  return <Button className='btn-report-default' label={localization.getText('COMMON_LABEL_CANCEL')} onClick={cancelDashboard} />;
});

const PresentationButton = connect()(function(props: any) {
  const {
    store,
    store: {
      dashboardDesignerMode: buttonStatus,
      dashboardDetail: { dashboard }
    },
    localization
  } = props;
  const isEnabled = !isEmpty(dashboard.activeDashboardParts);

  // Presentation class name.
  const preClassName = classnames('btn btn-primary', 'btn-report-default', {
    'izenda-Button_active': buttonStatus.isPresentation
  });

  const onClick = () => store.updateUseCache(true).then(buttonStatus.togglePresentation.bind(buttonStatus));

  return (
    <Button
      className={preClassName}
      title={localization.getText('DASHBOARD_LABEL_PRESENTATION_MODE')}
      onClick={onClick}
      disabled={buttonStatus.isDisabledPresentationButton || !isEnabled}
    >
      <i className='icon-slideshow' />
    </Button>
  );
});

const CloseButton = connect()(function(props: any) {
  const {
    store,
    store: { dashboardDesignerMode: buttonStatus },
    localization,
    location,
    router
  } = props;

  const confirmDirty = callback => {
    const isDirty = props.store.isDirty;
    props.confirmDirtyForm(isDirty).then(() => callback());
  };

  // disabled button:
  if (buttonStatus.isDisabledCloseButton) {
    return <Button className='btn-report-default'
                   aria-label='Close'
                   disabled
                   label={localization.getText('COMMON_LABEL_CLOSE')} />;
  }
  // history -> back
  if (get('state.fromList', location)) {
    return (
      <Button
        className='btn btn-primary btn-report-default'
        onClick={() => confirmDirty(() => router.goBack.bind(router))}
        disabled={buttonStatus.isDisabledCloseButton}
        aria-label='Close'
      >
        {localization.getText('COMMON_LABEL_CLOSE')}
      </Button>
    );
  }

  // Go to previous category
  if (String.isGuidFormat(get('dashboardDetail.dashboard.id', store))) {
    const dashboardDetail = getOr({}, 'dashboardDetail.dashboard', store);
    let categoryId = null;

    const assignCategoryId = function(catId) {
      String.isGuidFormat(catId) && catId !== DashboardConstants.EMPTY_GUID && (categoryId = catId);
    };

    assignCategoryId(dashboardDetail.categoryId);
    assignCategoryId(dashboardDetail.subCategoryId);

    return (
      <Button
        onClick={() => confirmDirty(() => router.push(routeUtils.getDashboardUrl('List', { catId: categoryId })))}
        className='btn btn-primary btn-report-default'
        disabled={buttonStatus.isDisabledCloseButton}
        aria-label='Close'
      >
        {localization.getText('COMMON_LABEL_CLOSE')}
      </Button>
    );
  }

  // Go to dashboard list root
  return (
    <Button
      onClick={() => confirmDirty(() => router.push(routeUtils.getDashboardUrl()))}
      className='btn btn-primary btn-report-default'
      disabled={buttonStatus.isDisabledCloseButton}
      aria-label='Close'
    >
      {localization.getText('COMMON_LABEL_CLOSE')}
    </Button>
  );
});

/**
 * save dashboard popup
 */
const SaveDashboardPopup = connect()(function(props: any) {
  const {
    clonedModel,
    clonedModel: {
      dashboardDetail,
      dashboardDetail: {
        dashboard: { name: reportName, isGlobal, categoryId, categoryName, subCategoryId, subCategoryName },
        saveAs
      }
    },
    store
  } = props;

  const model = {
    reportName,
    isGlobal,
    categoryId,
    categoryName,
    subCategoryId,
    subCategoryName,
    saveAs
  };

  const closeSavePopup = function() {
    props.closeSavePopup();
    props.onReject();
  };

  return (
    <SavePopup
      model={model}
      changeName={dashboardDetail.changeName.bind(dashboardDetail)}
      changeCategoryType={dashboardDetail.changeCategoryType.bind(dashboardDetail)}
      changeCategory={dashboardDetail.changeCategory.bind(dashboardDetail)}
      addCategory={clonedModel.addCategory.bind(clonedModel)}
      updateSubCategory={dashboardDetail.updateSubCategory.bind(dashboardDetail)}
      addSubCategory={clonedModel.addSubCategory.bind(clonedModel, categoryId)}
      getCategoriesFunc={store.lazyLoadCategories}
      getSubcategoriesFunc={store.lazyLoadSubCategories}
      validate={store.validateDashboardName.bind(store, dashboardDetail)}
      onClose={closeSavePopup}
      save={props.saveDashboard}
      popupConfig={DashboardConstants.SAVE_POPUP_CONFIG}
      store={store}
      isDashboard
    />
  );
});

@connect(actions)
export default class DashboardDesignerToolbar extends Component {

  constructor(props: any) {
    super(props);
  }
  static propTypes: {
    store: PropTypes.object
  };

  addDashboardTile = () => {
    const dashboardDetail = this.props.store.dashboardDetail;
    this.props.store.updateUseCache(true);
    dashboardDetail.addDashboardTile();
  };

  removeIntegrationMethod = () => {
    invokeArgs('unregisterExposeMethod', [['updateResults']], this.props);
  };

  exposeIntegrationMethod = (props: any) => {
    const method = {
      updateResults: this.updateResults
    };

    invokeArgs('registerExposeMethod', [method], props);
  };

  componentDidMount() {
    this.exposeIntegrationMethod(this.props);
  }

  componentWillReceiveProps(nextProps: any) {
    if (this.props.registerExposeMethod !== nextProps) {
      this.removeIntegrationMethod();
      this.exposeIntegrationMethod(nextProps);
    }
  }

  componentWillUnmount() {
    this.removeIntegrationMethod();
  }

  /**
   * Update result
   */
  updateResults = () => this.reloadData(true);

  refresh = () => this.reloadData(false);

  reloadData = (useCache: Boolean) => {
    const props = this.props;
    const { loader, store } = props;
    const clonedModel = this.clonedModel || {};

    loader.startLoading();

    return store
      .validateDashboard(clonedModel)
      .then(function(result) {
        if (result.success) {
          return store.updateUseCache(useCache).then(() => store.updateResults());
        } else {
          if (result.messages[0].detail) {
            return props.confirmFn('Information', {
              description: DashboardConstants.validateFilterMessage(result),
              type: 'error',
              isHTMLFormat: true,
              showCloseIndicator: true
            });
          } else {
            return props.validation.updateErrors(result.messages);
          }
        }
      })
      .finally(loader.endLoading.bind(loader));
  }

  /**
   * Save dashboard
   */
  @action
  saveDashboard = () => {
    const props = this.props;
    const { localization, loader, store, validation, router } = props;
    const clonedModel = this.clonedModel || {};
    const dashboardDetail = clonedModel.dashboardDetail || {};

    const saveDashboard = () => {
      return validation.validate().then(success => {
        if (success) {
          loader.startLoading();
          return store
            .validateDashboard(clonedModel)
            .then(result => {
              if (result.success) {
                return store.saveDashboard(clonedModel).then(saveResult => {
                  const errorMessage = get('messages', saveResult);

                  if (errorMessage) {
                    return props.confirmMessage('info', errorMessage[0].messages[0]);
                  } else if (saveResult.success) {
                    // Reset alert message
                    store.dashboardDesignerMode.resetAlertDetails();

                    const newLocationId = get('dashboard.id', saveResult);
                    const oldLocationId = get('location.query.id', saveResult);

                    newLocationId !== oldLocationId &&
                      router.push({
                        pathname: routeUtils.getDashboardUrl('Edit', {
                          id: newLocationId
                        }),
                        state: {
                          keepState: true
                        }
                      });
                  }

                  return saveResult;
                });
              }
              if (result.messages[0].detail) {
                return props.confirmFn('Information', {
                  description: DashboardConstants.validateFilterMessage(result),
                  type: 'error',
                  isHTMLFormat: true,
                  showCloseIndicator: true
                });
              } else {
                validation.updateErrors(result.messages);
              }
              return result.success;
            })
            .finally(loader.endLoading.bind(loader))
            .return(success);
        }

        return success;
      });
    };

    const savingConfirm = () =>
      props
        .confirmFn('Confirmation', {
          description: localization.getText('COMMON_MESSAGES_CONFIRM_SAVE')
        })
        .then(saveDashboard);

    if (!store.isDirty && !dashboardDetail.saveAs) {
      store.dashboardDesignerMode.setAlertDetails({
        success: true,
        message: localization.getText('COMMON_MESSAGES_NO_CHANGES_FOUND')
      });
    } else {
      const dashboardModel = !dashboardDetail.saveAs ? store.model : this.clonedModel;
      if (dashboardModel.dashboardDetail.dashboard.isGlobal) {
        const dashboardDefinition = dashboardModel.dashboardDetail.getDashboardDefinition();
        return store.validateGlobalDashboard(dashboardDefinition).then(response => {
          if (response.success) {
            return savingConfirm();
          } else {
            return props.confirmFn('Error', {
              description: response.messages[0].messages[0],
              showCancel: false
            });
          }
        });
      }
      return savingConfirm();
    }
  };

  /**
   * Close save popup
   */
  closeSavePopup = () => {
    this.clonedModel = null;
  };

  /**
   * render save control
   */
  renderSaveControl() {
    const onClickSave = (saveAs: boolean) => {
      const props = this.props;
      const { validation, store } = this.props;

      validation.validate().then(result => {
        if (result) {
          store.validateAccesses().then(result => {
            if (result.success) {
              const canSave = saveAs ? hasSaveAsPrivilege : hasSavePrivilege;
              this.clonedModel = store.model.clone(saveAs);

              canSave &&
                props.openPopup(SaveDashboardPopup, {
                  clonedModel: this.clonedModel,
                  closeSavePopup: this.closeSavePopup,
                  saveDashboard: this.saveDashboard,
                  store,
                  save: this.saveDashboard
                });
            } else {
              validation.updateErrors(result.messages);
            }
          });
        }
      });
    };

    const {
      authorization,
      authorizationConstants: { PERMISSION_KEY: permissionKeys },
      newDashboard,
      localization,
      store
    } = this.props;
    const hasSavePrivilege =
      newDashboard || authorization.checkPrivileges([permissionKeys.DashboardsActionsOverwriteExistingDashboard]);

    const hasSaveAsPrivilege = authorization.checkPrivileges([permissionKeys.DashboardsCanCreateNewDashboardValue]);

    const accessPriority = store.dashboardDetail.dashboard.accessPriority;
    const isSystemAdmin = uiState.currentUser.systemAdmin;
    const isSystemLevel = isNil(uiState.tenantId);
    const boardDetail = getOr({}, 'dashboard', store.model.dashboardDetail);

    const doesShowSaveBtn =
      hasSavePrivilege &&
      accessPriority === ACCESS_RIGHT.FullAccess &&
      !(boardDetail.isGlobal && !isSystemAdmin) &&
      !(boardDetail.isGlobal && !isSystemLevel);

    const doesShowSaveAsBtn =
      hasSaveAsPrivilege && (accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs);

    if (store.dashboardDesignerMode.isEdit) {
      return (
        (doesShowSaveBtn || doesShowSaveAsBtn) && (
          <div className='btn-group'>
            <button
              ref={function(dom) {
                dom && $izenda(dom).dropdown();
              }}
              className='btn btn-primary btn-report-default dropdown-toggle'
              data-toggle='dropdown'
            >
              {localization.getText('COMMON_LABEL_SAVE')}&nbsp;
              <span className='caret' />
            </button>
            <ul className='dropdown-menu'>
              {doesShowSaveBtn && (
                <li>
                  <a onClick={this.saveDashboard}>{localization.getText('COMMON_LABEL_SAVE')}</a>
                </li>
              )}
              {doesShowSaveAsBtn && (
                <li>
                  <a onClick={onClickSave.bind(this, true)}>{localization.getText('COMMON_LABEL_SAVE_AS')}</a>
                </li>
              )}
            </ul>
          </div>
        )
      );
    }

    return (
      hasSavePrivilege && (
        <Button
          label={DashboardConstants.TOOLBAR_CONTROL[4].label}
          onClick={onClickSave.bind(this, false)}
          keepDisplay
          className='btn-report-default'
        />
      )
    );
  }

  /**
   * Open Email Popup
   */
  openEmailPopup = () => {
    const store = this.props.store;
    const { dashboardDetail } = store;
    const data = {
      dashboardId: dashboardDetail.dashboard.id
    };
    const subscriptionData = new SubscriptionDetails(data);

    const handleCloseEmailPopup = email => {
      email && (email.subscriptionFilterFields = []);
      email && (email.subscriptionCommonFilterFields = subscriptionData.subscriptionCommonFilterFields);
      return email && store.sendEmail(store.dashboardDetail.dashboard.id, email);
    };
    this.props.openPopup(EmailPopup, {
      onClosed: handleCloseEmailPopup,
      type: EMAIL_TYPE.dashboard,
      dashboardId: dashboardDetail.dashboard.id || DashboardConstants.EMPTY_GUID,
      dashboardDetail,
      data: subscriptionData
    });
  };

  /**
   * render email control
   */
  renderEmailControl = (isDisabled: boolean) => {
    return (
      <Button
        className='btn-report-default'
        label={this.props.localization.getText('COMMON_LABEL_EMAIL')}
        disabled={isDisabled}
        onClick={this.openEmailPopup}
      />
    );
  };

  /**
   * on click background Color button
   */
  onClickBackgroundColor = () => {
    const { dashboardDetail } = this.props.store;

    return this.props.openPopup(ColorPickerPopup, {
      color: get('dashboard.backgroundColor', dashboardDetail),
      onAcceptColor: dashboardDetail.setBackgroundColor.bind(dashboardDetail)
    });
  };

  /**
   * handle click on dashboard description.
   */
  onClickDashboardDescription = () => {
    const store = this.props.store;
    const dashboardDetail = store.dashboardDetail || {};
    const dashboard = dashboardDetail.dashboard || {};
    const description = cloneDeep(dashboard.description);
    const CommonPopup = commonPopup('DashboardDescription', dashboardDescriptionPropTypes);

    return this.props.openPopup(CommonPopup, {
      Content: DashboardDescriptionContent,
      constants: dashboardDescriptionConstants,
      description: description,
      save: this.onSaveDashboardDescription
    });
  };

  /**
   * handle click on Set Background Image.
   */
  onClickBackgroundImage = () => {
    const store = this.props.store;
    const dashboardDetail = store.dashboardDetail || {};
    const dashboard = dashboardDetail.dashboard || {};
    const imageUrl = cloneDeep(dashboard.imageUrl);
    const stretchImage = dashboard.stretchImage;
    const CommonPopup = commonPopup(backgroundImagePropTypes);

    return this.props.openPopup(CommonPopup, {
      Content: BackgroundImageContent,
      constants: backgroundImageConstants,
      imageUrl: imageUrl,
      stretchImage: stretchImage,
      save: this.onSaveBackgroundImage,
      store: store
    });
  };

  /**
   * Build edit dropdown element
   */
  buildEditDropdown = dom => {
    dom && $izenda(dom).dropdown();
  };

  /**
   * render edit control
   * @param  {boolean} isDisabled
   */
  renderEditControl = (isDisabled: boolean) => {
    const {
      localization,
      authorization,
      authorizationConstants: { PERMISSION_KEY: permissionKeys },
      store,
      store: { dashboardDetail }
    } = this.props;
    const isEditMode = store.dashboardDesignerMode.isEdit;
    const showFilterDescription = get('dashboard.showFilterDescription', dashboardDetail);
    const accessPriority = dashboardDetail.dashboard.accessPriority;

    const dashboardModel = getOr({}, 'dashboard', dashboardDetail);

    const hasOverwritePrivilege = authorization.checkPrivileges([
      permissionKeys.DashboardsActionsOverwriteExistingDashboard
    ]);

    const hasCopyPrivilege = authorization.checkPrivileges([permissionKeys.DashboardsCanCreateNewDashboardValue]);

    const doesShowDashboardDescriptionBtn =
      accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs;

    const doesShowBackgroundColorBtn =
      accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs;

    const doesShowBackgroundImageBtn =
      accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs;

    const doesShowCopyBtn =
      (accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs) &&
      isEditMode &&
      hasCopyPrivilege &&
      dashboardModel.copyable;

    const doesShowMoveBtn =
      accessPriority === ACCESS_RIGHT.FullAccess && hasOverwritePrivilege && isEditMode && dashboardModel.movable;

    const doesShowFilterDescriptionBtn =
      accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs;

    return (
      (doesShowDashboardDescriptionBtn ||
        doesShowBackgroundColorBtn ||
        doesShowBackgroundImageBtn ||
        doesShowCopyBtn ||
        doesShowMoveBtn ||
        doesShowFilterDescriptionBtn) && (
        <div className='btn-group'>
          <button
            ref={this.buildEditDropdown}
            className='btn btn-primary btn-report-default dropdown-toggle'
            disabled={isDisabled}
            data-toggle='dropdown'
          >
            {localization.getText('COMMON_LABEL_EDIT')}&nbsp;
            <span className='caret' />
          </button>
          <ul className='dropdown-menu'>
            {doesShowDashboardDescriptionBtn && (
              <li>
                <a onClick={this.onClickDashboardDescription}>
                  {localization.getText('DASHBOARD_LABEL_DASHBOARD_DESCRIPTION')}
                </a>
              </li>
            )}
            {doesShowBackgroundColorBtn && (
              <li>
                <a onClick={this.onClickBackgroundColor}>
                  {localization.getText('DASHBOARD_LABEL_SET_BACKGROUND_COLOR')}
                </a>
              </li>
            )}
            {doesShowBackgroundImageBtn && (
              <li>
                <a onClick={this.onClickBackgroundImage}>
                  {localization.getText('DASHBOARD_LABEL_SET_BACKGROUND_IMAGE')}
                </a>
              </li>
            )}
            {doesShowCopyBtn && (
              <li>
                <a onMouseDown={this.copyDashboard}>{localization.getText('COMMON_LABEL_COPY')}</a>
              </li>
            )}
            {doesShowMoveBtn && (
              <li>
                <a onMouseDown={this.moveDashboard}>{localization.getText('COMMON_LABEL_MOVE')}</a>
              </li>
            )}
            {doesShowFilterDescriptionBtn && (
              <li>
                <a onMouseDown={dashboardDetail.toggleShowFilterDescription.bind(dashboardDetail)}>
                  {showFilterDescription
                    ? localization.getText('COMMON_LABEL_HIDE')
                    : localization.getText('COMMON_LABEL_SHOW')}{' '}
                  {localization.getText('DASHBOARD_MESSAGE_FILTER_DESCIPTIONS_ON_TILE')}
                </a>
              </li>
            )}
          </ul>
        </div>
      )
    );
  };

  /**
   * Get action function base on move or copy2
   * @param  {string} action
   * @param  {Object} data dashboard data
   */
  getActionFunc = (action: string, data: any) => {
    const {
      store,
      store: { copyDashboard, moveDashboard }
    } = this.props;
    const functionCall = action === DashboardConstants.COPY_KEY ? copyDashboard : moveDashboard;

    return functionCall.bind(store)(data);
  };

  /**
   * Open popup
   * @param  {int} action type
   */
  openPopup = (action: number) => {
    const props = this.props;
    const {
      store,
      authorization,
      authorizationConstants: { PERMISSION_KEY: permissionKeys },
      store: { model }
    } = props;
    const boardDetail = model.dashboardDetail.dashboard || {};
    const dashboard = {
      name: boardDetail.name,
      id: boardDetail.id,
      isGlobal: boardDetail.isGlobal
    };

    props
      .openPopup(ActionPopup, {
        dashboard: dashboard,
        popupConfig: DashboardConstants.SAVE_POPUP_CONFIG,
        action: action,
        actionFunc: this.getActionFunc.bind(this, action),
        getCategoriesFunc: store.lazyLoadCategories,
        getSubcategoriesFunc: store.lazyLoadSubCategories,
        hasCanCreateNewCategoryPrivilege: authorization.checkPrivileges([
          permissionKeys.DashboardsCanCreateNewCategoryValue
        ])
      })
      .then(function(message) {
        return props.confirmMessage('info', message);
      });
  };

  /**
   * Handle when click copy button
   */
  copyDashboard = () => {
    this.openPopup(DashboardConstants.COPY_KEY);
  };

  /**
   * Handle when click move button
   */
  moveDashboard = () => {
    this.openPopup(DashboardConstants.MOVE_KEY);
  };

  /**
   * Handle save dashboard description.
   * @param {String} value
   */
  onSaveDashboardDescription = (value: string) => {
    const dashboardDetail = getOr({}, 'dashboardDetail', this.props.store);

    dashboardDetail.setDescription(value);
  };

  /**
   * Handle save background image.
   * @param {String} imageUrl
   * @param {Boolean} stretchImage
   */
  onSaveBackgroundImage = (imageUrl: string, stretchImage: boolean) => {
    const dashboardDetail = getOr({}, 'dashboardDetail', this.props.store);

    dashboardDetail.setBackgroundImage(imageUrl, stretchImage);
  };

  /**
   * Handle Print
   */
  handlePrint = () => {
    const {
      store,
      store: {
        model: {
          dashboardDetail: {
            dashboard: { id: dashboardId }
          }
        }
      }
    } = this.props;

    store.saveDraftDataForPrint().then(function(draftId) {
      const url = routeUtils.getViewerUrl('Dashboard', { id: dashboardId });

      BrowserUtils.openNewTab(routeUtils.makeAbsoluteUrl(url, 'print=true&draftId=' + draftId));
      return null;
    });
  };

  /**
   * Handle when click access button
   */
  onClickAccess = () => {
    const {
      store,
      store: { dashboardDesignerMode: buttonStatus },
      validation
    } = this.props;

    if (buttonStatus.isAccess) {
      validation.validate().then(result => {
        if (result) {
          store.validateAccesses().then(result => {
            if (result.success) {
              buttonStatus.toggleAccess();
            } else {
              validation.updateErrors(result.messages);
            }
          });
        }
      });
    } else {
      buttonStatus.toggleAccess();
      store.initAccess();
    }
  };

  render() {
    const props = this.props;
    const {
      isLoaded,
      localization,
      authorization,
      authorizationConstants: { PERMISSION_KEY: permissionKeys },
      store,
      id,
      store: { dashboardDesignerMode: buttonStatus }
    } = props;

    if (get('integrationStyle.hideDashboardToolbar', props) || !isLoaded)
      return null;

    const name = getOr('', 'dashboardDetail.dashboard.name', store);
    const isAccess = buttonStatus.isAccess;
    const isSchedule = buttonStatus.isSchedule;
    const isSubscribe = buttonStatus.isSubscribe;
    const isAccessOrScheduleOrSubscribe = isAccess || isSchedule || isSubscribe;
    const accessPriority = store.dashboardDetail.dashboard.accessPriority;

    // licences
    const hasScheduleLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Scheduling);
    const hasExportLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Exporting);

    // privileges
    const hasSchedulePrivilege = authorization.checkPrivileges([permissionKeys.DashboardsActionsSchedule]);
    const hasSubscribePrivilege = authorization.checkPrivileges([permissionKeys.DashboardsActionsSubscribe]);
    const hasAccessPrivilege = authorization.checkPrivileges([permissionKeys.DashboardsActionsConfigureAccessRights]);
    const hasEmailPrivilege = authorization.checkPrivileges([permissionKeys.DashboardsActionsEmail]);
    const hasPrintPrivilege = authorization.checkPrivileges([permissionKeys.DashboardsActionsPrint]);
    const hasExportPrivilege = authorization.checkPrivileges([permissionKeys.DashboardsActionsExporting]);

    const restProps = omit(['registerExposeMethod', 'unregisterExposeMethod', 'integrationStyle'], props);
    const hideIntegratedButton = get('integrationStyle.hideIntegratedButton', props);

    const showAccessButton =
      hasAccessPrivilege &&
      (accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs);
    const showSubscribeButton =
      hasScheduleLicense &&
      hasSubscribePrivilege &&
      (
        ((accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs) && !hasSchedulePrivilege)
        ||
        (accessPriority === ACCESS_RIGHT.Locked || accessPriority === ACCESS_RIGHT.ViewOnly)
      );
    const showScheduleButton =
      hasScheduleLicense &&
      hasSchedulePrivilege &&
      (accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs) &&
      !store.dashboardDetail.dashboard.isGlobal;

    return (
      <div className='u_controlHeight u_marginBottom u_clearFix'>
        <div className='izenda-ActionBarRight'>
          <ActionButton>
            <PresentationButton {...restProps} />
            {showAccessButton && (
                <ToggleButton
                  {...restProps}
                  label={DashboardConstants.TOOLBAR_CONTROL[1].label}
                  checkActive={get('isAccess')}
                  checkDisable={get('isDisabledAcessButton')}
                  toggleAction={this.onClickAccess}
                />
              )}
            {showSubscribeButton && (
                <ToggleButton
                  {...restProps}
                  label={DashboardConstants.TOOLBAR_CONTROL[2].label}
                  checkActive={get('isSubscribe')}
                  checkDisable={get('isDisabledSubscribeButton')}
                  toggleAction={buttonStatus.toggleSubscribe.bind(buttonStatus)}
                />
              )}
            {showScheduleButton && (
                <ToggleButton
                  {...restProps}
                  label={DashboardConstants.TOOLBAR_CONTROL[3].label}
                  checkActive={get('isSchedule')}
                  checkDisable={get('isDisabledScheduleButton')}
                  toggleAction={buttonStatus.toggleSchedule.bind(buttonStatus)}
                />
              )}
            {this.renderEditControl(isAccessOrScheduleOrSubscribe)}
            {hasExportLicense && hasPrintPrivilege && !uiState.isMobile && (
              <PrintDashboardButton
                disabled={isAccessOrScheduleOrSubscribe}
                id={id}
                name={name}
                store={store}
                isDesignerMode
                export={store.printDashboard}
              />
            )}
            {hasExportLicense && hasExportPrivilege && !uiState.isMobile && (
              <ExportButton
                id={id}
                isDashboard
                name={name}
                store={store}
                isDesignerMode
                export={store.exportDashboard}
              />
            )}
            {hasEmailPrivilege && this.renderEmailControl(isAccessOrScheduleOrSubscribe)}
            {store.isCacheEnable &&(
              <RefreshDataButton disabled={isAccessOrScheduleOrSubscribe} updateResults={this.refresh} />
            )}
            
            {(accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs) && (
              <Button
                label={localization.getText('DASHBOARD_LABEL_ADD_DASHBOARD_TILE')}
                disabled={isAccessOrScheduleOrSubscribe}
                onClick={this.addDashboardTile}
                className='btn-success'
              />
            )}

            {this.renderSaveControl()}

            {(accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs) && (
              <CancelButton {...restProps} keepDisplay />
            )}

            {!hideIntegratedButton && (
              <CloseButton {...restProps} />
            )}
          </ActionButton>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Dashboard/DashboardDesigner/Izenda.DashboardDesignerToolbar.js